import { createContext, useEffect, useState, useRef, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

interface UserProfile {
  id: string;
  username: string;
  created_at: string;
  updated_at: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  loading: boolean;
  needsUsername: boolean;
  signInWithApple: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, username: string) => Promise<void>;
  signOut: () => Promise<void>;
  setUsername: (username: string) => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [needsUsername, setNeedsUsername] = useState(false);

  // Flag to prevent race condition during signup
  const skipNextProfileLoad = useRef(false);

  // Fetch user profile from public.users table
  const fetchProfile = async (userId: string): Promise<UserProfile | null> => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        console.log('Error fetching profile:', error.message);
        return null;
      }
      return data as UserProfile | null;
    } catch (error) {
      console.error('Error fetching profile:', error);
      return null;
    }
  };

  // Load profile separately (non-blocking)
  const loadProfile = (userId: string) => {
    fetchProfile(userId).then((userProfile) => {
      setProfile(userProfile);
      setNeedsUsername(!userProfile);
    });
  };

  useEffect(() => {
    // Use the exact same pattern as the original working code
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        loadProfile(session.user.id);
      }
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (skipNextProfileLoad.current) {
        // Skip profile load during signup (we handle it ourselves)
        skipNextProfileLoad.current = false;
        setLoading(false);
      } else if (session?.user) {
        loadProfile(session.user.id);
      } else {
        setProfile(null);
        setNeedsUsername(false);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Sign in with Apple OAuth
  const signInWithApple = async () => {
    await supabase.auth.signInWithOAuth({
      provider: 'apple',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });
  };

  // Sign in with email and password
  const signInWithEmail = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
  };

  // Sign up with email, password, and username
  const signUp = async (email: string, password: string, username: string) => {
    // Set flag to prevent onAuthStateChange from overwriting our state
    skipNextProfileLoad.current = true;

    // First, create the auth user
    const { data, error: signUpError } = await supabase.auth.signUp({
      email,
      password,
    });

    if (signUpError) throw signUpError;

    if (!data.user) {
      throw new Error('Failed to create account');
    }

    // Set the session if available (may be null if email confirmation is required)
    if (data.session) {
      await supabase.auth.setSession(data.session);
    }

    // Create the profile with username
    const { error: profileError } = await supabase
      .from('users')
      .insert({
        id: data.user.id,
        username: username.trim(),
      });

    if (profileError) {
      // Sign out since profile creation failed
      await supabase.auth.signOut();
      throw new Error(profileError.message);
    }

    // Fetch the newly created profile
    const newProfile = await fetchProfile(data.user.id);
    setProfile(newProfile);
    setNeedsUsername(false);
  };

  // Set or update username (works for both new profiles and username changes)
  const setUsername = async (username: string) => {
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('users')
      .upsert({
        id: user.id,
        username: username.trim(),
      }, {
        onConflict: 'id',
      });

    if (error) {
      throw new Error(error.message);
    }

    // Refresh profile
    const newProfile = await fetchProfile(user.id);
    setProfile(newProfile);
    setNeedsUsername(false);
  };

  // Sign out
  const signOut = async () => {
    // Clear all Supabase auth data from localStorage
    const keysToRemove = Object.keys(localStorage).filter(
      (key) => key.startsWith('sb-') && key.endsWith('-auth-token')
    );
    keysToRemove.forEach((key) => localStorage.removeItem(key));

    await supabase.auth.signOut({ scope: 'local' });

    setUser(null);
    setSession(null);
    setProfile(null);
    setNeedsUsername(false);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        loading,
        needsUsername,
        signInWithApple,
        signInWithEmail,
        signUp,
        signOut,
        setUsername,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
